import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {children}
    </div>
  );
}

function P({ children, caps }) {
  return <p className={`text-sm text-muted-foreground leading-relaxed ${caps ? 'uppercase text-xs' : ''}`}>{children}</p>;
}

function UL({ children }) {
  return <ul className="text-sm text-muted-foreground space-y-1.5 list-disc list-inside">{children}</ul>;
}

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-12">
      <Button variant="ghost" onClick={() => navigate(-1)}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>

      <div>
        <h1 className="text-2xl font-bold">TruContext Terms of Service</h1>
        <p className="text-sm text-muted-foreground mt-1">Effective Date: March 22, 2026 | Last Updated: March 22, 2026</p>
      </div>

      <P>These Terms of Service ("Terms") govern your access to and use of the TruContext platform, APIs, CLI tools, and related services (collectively, the "Service") provided by Alpha Collective LLC ("Company," "we," "us," or "our").</P>
      <P>By creating an account, accessing the Service, or using the CLI tool, you agree to be bound by these Terms. If you do not agree, do not use the Service.</P>

      <Section title="1. Your Data, Your Ownership">
        <P><strong className="text-foreground">You own your data.</strong> All content, entities, relationships, and knowledge graph data that you or your applications create, ingest, or store through the Service ("Your Data") remains your property. We claim no ownership rights over Your Data.</P>
        <P>We will not use Your Data to train machine learning models, improve our AI systems, or for any purpose beyond providing and maintaining the Service to you — unless you provide separate, explicit written consent.</P>
      </Section>

      <Section title="2. What We Do With Your Data">
        <P>We process Your Data solely to provide the Service:</P>
        <UL>
          <li><strong className="text-foreground">Storage:</strong> Your Data is stored in encrypted databases (DynamoDB, Neo4j, S3) within AWS infrastructure.</li>
          <li><strong className="text-foreground">Processing:</strong> When you ingest content, our AI pipeline extracts concepts, relationships, and patterns as part of the Service's core functionality.</li>
          <li><strong className="text-foreground">Heartbeat:</strong> Our background intelligence layer analyzes patterns in Your Data to surface insights, manage confidence decay, and generate attention directives — all within your tenant boundary.</li>
        </UL>
        <P>We do not access, review, or analyze Your Data for any purpose other than operating the Service, unless required by law or with your explicit consent.</P>
      </Section>

      <Section title="3. Account and Security">
        <UL>
          <li>You are responsible for maintaining the security of your account credentials.</li>
          <li>You must not share API keys or authentication tokens with unauthorized parties.</li>
          <li>You are responsible for all activity that occurs under your account.</li>
          <li>You must notify us promptly of any unauthorized access to your account.</li>
        </UL>
      </Section>

      <Section title="4. Acceptable Use">
        <P>You agree not to:</P>
        <UL>
          <li>Use the Service for any unlawful purpose or in violation of any applicable laws.</li>
          <li>Ingest content that you do not have the right to use or that infringes third-party intellectual property rights.</li>
          <li>Attempt to access another user's data, circumvent tenant isolation, or probe the Service's security.</li>
          <li>Use the Service to store or process data subject to specific regulatory requirements (e.g., HIPAA, PCI-DSS) unless you have obtained a separate written agreement with us.</li>
          <li>Reverse engineer, decompile, or attempt to extract the source code of our proprietary systems.</li>
          <li>Resell, redistribute, or sublicense access to the Service without our written consent.</li>
        </UL>
      </Section>

      <Section title="5. Service Availability">
        <P>We strive to maintain high availability but do not guarantee uninterrupted access. The Service is provided on an "as available" basis. We may perform maintenance, updates, or modifications that temporarily affect availability.</P>
      </Section>

      <Section title="6. API Usage and Rate Limits">
        <UL>
          <li>API usage is subject to rate limits and quotas based on your plan tier.</li>
          <li>We reserve the right to throttle or suspend access if usage patterns indicate abuse or threaten the stability of the Service.</li>
          <li>Plan tiers, pricing, and associated limits are published on our website and may be updated with reasonable notice.</li>
        </UL>
      </Section>

      <Section title="7. Intellectual Property">
        <UL>
          <li>The Service, including its architecture, algorithms, documentation, recipes, and all related intellectual property, is owned by Alpha Collective LLC.</li>
          <li>Your use of the Service does not grant you any rights to our intellectual property except the limited right to use the Service in accordance with these Terms.</li>
          <li>The TruContext CLI is licensed under the MIT License. This license applies to the CLI software only, not to the Service.</li>
        </UL>
      </Section>

      <Section title="8. Limitation of Liability">
        <P caps>To the maximum extent permitted by law, in no event shall Alpha Collective LLC be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, or goodwill.</P>
        <P caps>Our total aggregate liability shall not exceed the greater of (a) the amounts you paid to us in the twelve months preceding the claim, or (b) one hundred dollars ($100).</P>
      </Section>

      <Section title="9. Indemnification">
        <P>You agree to indemnify, defend, and hold harmless Alpha Collective LLC from and against any claims arising out of your use of the Service, violation of these Terms, or violation of any third-party rights.</P>
      </Section>

      <Section title="10. Disclaimer of Warranties">
        <P caps>The Service is provided "as is" and "as available" without warranties of any kind. We do not warrant that the Service will be uninterrupted, error-free, or secure. AI-generated outputs are provided for informational purposes and should not be relied upon as the sole basis for decisions.</P>
      </Section>

      <Section title="11. Termination">
        <UL>
          <li>You may terminate your account at any time.</li>
          <li>We may suspend or terminate your access if you violate these Terms.</li>
          <li>Upon termination, you may request export of Your Data within 30 days.</li>
        </UL>
      </Section>

      <Section title="12. Changes to These Terms">
        <P>We may update these Terms from time to time. We will notify you of material changes via email or through the Service.</P>
      </Section>

      <Section title="13. Governing Law">
        <P>These Terms are governed by the laws of the State of Colorado, United States. Any disputes shall be resolved in the state or federal courts located in Colorado.</P>
      </Section>

      <Section title="14. Contact">
        <P><strong className="text-foreground">Alpha Collective LLC</strong><br />Email: legal@alphacollective.com<br />Website: trucontext.ai</P>
      </Section>
    </div>
  );
}
